import {useFetchAllObjsWithRefetchCapability} from "../../platform/hooks/PlatformTypeScriptHooks";
import UboldSpinner from "../../ubold-components/UboldSpinner";
import React, { useState } from "react";
import {Card} from "components/Card";
import {UploadTechDraw} from "./UploadTechDraw";
import {useNavigate} from "react-router-dom";
import AjaxService from "../../platform/ajax/AjaxService";
import {TechDrawUserGuide} from "./TechDrawUserGuide";
import {useAuthenticatedImage} from "../../hooks/useAuthenticatedImage";

export type TechDrawsOverviewReport = {
    entries: TechDrawOverviewReportEntry[];
    missingDrawingNames: string[];
};

export type TechDrawOverviewReportEntry = {
    id: string;
    displayName: string;
    createdAt: number;
    updatedAt: number;
    rules?: TechDrawLabelRule[];
};

export type TechDrawLabelRule = {
    sourceIdentifier: string;
    xPosition: number;
    yPosition: number;
    textAlignment: 'LEFT' | 'CENTER' | 'RIGHT';
    textDirection: 'HORIZONTAL' | 'VERTICAL';
    relativeFontSize?: number;
};

function TechDrawThumbnail({id, alt}: { id: string; alt: string }) {
    const imageUrl = useAuthenticatedImage("/api/tech-draws/" + id + "/raw-image");
    if (imageUrl === null) {
        return <div className="text-muted" style={{width: "100px"}}>…</div>;
    }
    return <img src={imageUrl} alt={alt} style={{width: "100px"}}/>;
}

export function BrowseTechDrawsPage() {
    const navigate = useNavigate();
    const {result: draws, refetch} = useFetchAllObjsWithRefetchCapability<TechDrawsOverviewReport>("/api/tech-draws")
    const [isDeleting, setIsDeleting] = useState(false);
    const [drawToDelete, setDrawToDelete] = useState<string | null>(null);

    const handleEditClick = (id: string) => {
        navigate(`/edit-tech-draw/${id}`);
    };

    const handleDeleteClick = (id: string, displayName: string) => {
        if (window.confirm(`Czy na pewno chcesz usunąć rysunek "${displayName}"?`)) {
            setIsDeleting(true);
            setDrawToDelete(id);
            
            AjaxService.delete(`/api/tech-draws/${id}`)
                .then(response => {
                    if (response.ok) {
                        // Refresh the list after successful deletion
                        refetch();
                    } else {
                        console.error('Failed to delete tech draw', response);
                        alert('Nie udało się usunąć rysunku. Spróbuj ponownie.');
                    }
                })
                .catch(error => {
                    console.error('Error deleting tech draw:', error);
                    alert('Wystąpił błąd podczas usuwania rysunku.');
                })
                .finally(() => {
                    setIsDeleting(false);
                    setDrawToDelete(null);
                });
        }
    };

    const uploaderCard = <Card header={"Załaduj nowy rysunek"}>
        <UploadTechDraw onUploadSuccess={refetch}/>
    </Card>;

    if (draws === null) {
        return <Card><UboldSpinner/></Card>
    }

    const hasMissingDrawings = draws.missingDrawingNames && draws.missingDrawingNames.length > 0;

    if (draws.entries.length === 0) {
        return <>
            {hasMissingDrawings && (
                <Card additionalClassNames={"mb-5 alert-warning"}>
                    <div className="p-3">
                        <h5 className="alert-heading">Uwaga! Brakujące rysunki techniczne</h5>
                        <p>Znaleziono odniesienia do rysunków technicznych, które nie zostały jeszcze utworzone:</p>
                        <ul>
                            {draws.missingDrawingNames!.map((name, index) => (
                                <li key={index}><strong>{name}</strong></li>
                            ))}
                        </ul>
                        <hr />
                        <p className="mb-0">Aby rozwiązać ten problem, załaduj rysunki techniczne z wyżej wymienionymi nazwami.</p>
                    </div>
                </Card>
            )}
            <Card additionalClassNames={"mb-5"}>
                <div className="text-center fs-4 text-gray-600 mt-5 mb-5">Brak rezultatów</div>
            </Card>
            {uploaderCard}
        </>
    }

    return <>
        <TechDrawUserGuide/>
        {hasMissingDrawings && (
            <Card additionalClassNames={"mb-5 alert-warning"}>
                <div className="p-3">
                    <h5 className="alert-heading">Uwaga! Brakujące rysunki techniczne</h5>
                    <p>Znaleziono odniesienia do rysunków technicznych, które nie zostały jeszcze utworzone:</p>
                    <ul>
                        {draws.missingDrawingNames!.map((name, index) => (
                            <li key={index}><strong>{name}</strong></li>
                        ))}
                    </ul>
                    <hr />
                    <p className="mb-0">Aby rozwiązać ten problem, załaduj rysunki techniczne z wyżej wymienionymi nazwami.</p>
                </div>
            </Card>
        )}
        <Card>
            <table className={"table table-row-bordered table-row-gray-100 align-middle gs-0 gy-3"}>
                <thead>
                <tr className="fw-bolder text-muted">
                    <th>Podgląd</th>
                    <th className="w-100px">Nazwa</th>
                    <th className="min-w-120px">Utworzono</th>
                    <th className="min-w-120px">Zaktualizowano</th>
                    <th className="min-w-100px">Akcje</th>
                </tr>
                </thead>
                <tbody>
                {draws.entries.map(draw => (
                    <tr key={draw.id}>
                        <td>
                            <TechDrawThumbnail id={draw.id} alt={draw.displayName}/>
                        </td>
                        <td className="d-flex flex-column gap-2 align-items-start">
                            <div className="fw-boldest fs-5">{draw.displayName}</div>
                            <div className={"text-gray-200 text-nowrap"}>{draw.id}</div>
                        </td>
                        <td className="align-top">
                            <div className={"fw-boldest fs-5"}>{new Date(draw.createdAt).toLocaleString("pl-PL")}</div>
                        </td>
                        <td className="align-top">
                            <div className={"fw-boldest fs-5"}>{new Date(draw.updatedAt).toLocaleString("pl-PL")}</div>
                        </td>
                        <td className="align-top">
                            <div className="d-flex gap-2">
                                <button
                                    className="btn btn-sm btn-primary"
                                    onClick={() => handleEditClick(draw.id)}
                                >
                                    Edytuj
                                </button>
                                <button
                                    className="btn btn-sm btn-danger"
                                    onClick={() => handleDeleteClick(draw.id, draw.displayName)}
                                    disabled={isDeleting && drawToDelete === draw.id}
                                >
                                    {isDeleting && drawToDelete === draw.id ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                            Usuwanie...
                                        </>
                                    ) : (
                                        'Usuń'
                                    )}
                                </button>
                            </div>
                        </td>
                    </tr>
                ))}
                </tbody>
            </table>
        </Card>
        {uploaderCard}
    </>
}